import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { playerSlice } from "../redux/slices/playerSlice";
import { playerExtendedApi } from "../services";
import { playerListLimit } from "../shared/Constants";
import { Player } from "../types";
import { useParallelPaginatedLoading } from "./useParallelPaginatedLoading";

/**
 * Custom hook for player loading that fetches all player pages in parallel
 * when org player count is known. Stores the merged, deduped result in Redux.
 * The parallel fetch mechanics are shared via useParallelPaginatedLoading.
 */
export const usePlayerLoading = () => {
  const storedPlayerList = useAppSelector((state) => state.players.playerList);
  const orgId = useAppSelector((state) => state.org).id;
  const playerCount = useAppSelector((state) => state.org).players.total;
  const dispatch = useAppDispatch();
  const { storePlayers } = playerSlice.actions;

  const { isFetching, fetchError, loadComplete } =
    useParallelPaginatedLoading<Player>({
      orgId,
      count: playerCount,
      limit: playerListLimit,
      makeRequest: (offset, limit) =>
        dispatch(
          playerExtendedApi.endpoints.getAllPlayers.initiate({
            offset,
            limit,
          }),
        ),
      getId: (player) => player.orgPlayerId || player.id,
      alreadyLoadedCount: storedPlayerList.length,
      onLoaded: (players) => dispatch(storePlayers(players)),
    });

  return {
    storedPlayerList,
    fetchingPlayers: isFetching,
    fetchError,
    playerCount,
    playersLoadComplete: loadComplete,
  };
};
